import React, { useState, useContext } from 'react'
import { useMutation } from '@apollo/client'
import { Form, Button, Message, Confirm } from 'semantic-ui-react'
import { AuthContext, PasswordInput } from '@dexter-experts/auth-react'
import MessageContext from '../../contexts/MessageContext'
import DataContext from '../../contexts/DataContext'
import { UPDATE_EMAIL } from '../../graphql/user'

const PASSWORD_RULES = [
  {
    id: 'min-length',
    label: 'At least 8 characters',
    test: (value) => value.length >= 8,
  },
  {
    id: 'letter',
    label: 'At least 1 letter',
    test: (value) => /[A-Za-z]/.test(value),
  },
  {
    id: 'number',
    label: 'At least 1 number',
    test: (value) => /\d/.test(value),
  },
]

const Account = () => {
  const { logout, authUser, changePassword } = useContext(AuthContext)
  const { setAlertMsg } = useContext(MessageContext)
  const { advisor } = useContext(DataContext)

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState(advisor.email)
  const [passwordMessage, setPasswordMessage] = useState(null)
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [updateEmail, { error }] = useMutation(UPDATE_EMAIL, {
    context: { skipGlobalErrorHandling: true },
    onError: (error) => {
      setOpen(false)
      if (error.message.includes('Email already registered')) {
        setAlertMsg({
          title: 'That email is already in use',
          content: `${email} belongs to another account. Your email is unchanged — please try a different address.`,
        })
        return
      }
      setAlertMsg({ title: 'Error', content: error.message })
    },
    onCompleted: logout,
  })

  const MESSAGE = {
    header:
      'To update your email, please be aware of the following important details:',
    content:
      'You will be automatically logged out of your account. Upon logging back in, you will be prompted to verify your new email address for security purposes.',
  }
  const getCustomErrorMessage = (error) => {
    if (
      error.message.includes("Object didn't pass validation for format email")
    ) {
      return 'Please enter a valid email address.'
    }
    return error.message
  }

  const hasPassword = authUser?.hasPassword === true
  const passwordTitle = hasPassword ? 'Change password' : 'Password Setup'
  const passwordDescription = hasPassword
    ? 'Enter your current password and choose a new one.'
    : 'Create your first password to sign in without a magic link.'

  const savePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match.' })
      return
    }
    if (!PASSWORD_RULES.every((rule) => rule.test(newPassword))) {
      setPasswordMessage({
        type: 'error',
        text: 'Your password must be at least 8 characters and include a letter and a number.',
      })
      return
    }

    setIsSavingPassword(true)
    setPasswordMessage(null)
    try {
      await changePassword({
        currentPassword: hasPassword ? currentPassword : undefined,
        newPassword,
      })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordMessage({
        type: 'success',
        text: 'Password updated.',
      })
    } catch (passwordError) {
      setPasswordMessage({
        type: 'error',
        text: "We couldn't update your password. Please try again.",
      })
    } finally {
      setIsSavingPassword(false)
    }
  }

  return (
    <div>
      <div className="form-section">
        <Form>
          <h4>Email Address</h4>
          <Form.Input
            error={error ? { content: getCustomErrorMessage(error) } : false}
            value={email}
            onChange={(e, { value }) => setEmail(value)}
          />
        </Form>
        <Message warning>
          <Message.Header>{MESSAGE.header}</Message.Header>
          <p>{MESSAGE.content}</p>
        </Message>
        <Button negative content="Update email" onClick={() => setOpen(true)} />
      </div>
      <div className="form-section">
        {passwordMessage && (
          <Message
            positive={passwordMessage.type === 'success'}
            negative={passwordMessage.type === 'error'}
          >
            <Message.Content>{passwordMessage.text}</Message.Content>
          </Message>
        )}
        <h4>{passwordTitle}</h4>
        <p>{passwordDescription}</p>
        <Form>
          {hasPassword && (
            <PasswordInput
              required
              label="Current password"
              value={currentPassword}
              onChange={(event, { value }) => setCurrentPassword(value)}
            />
          )}
          <PasswordInput
            required
            label="New password"
            value={newPassword}
            onChange={(event, { value }) => setNewPassword(value)}
          />
          <div
            className="auth-password-rules"
            role="list"
            aria-label="Password requirements"
          >
            {PASSWORD_RULES.map((rule) => {
              const isMatched = rule.test(newPassword)
              return (
                <label
                  key={rule.id}
                  className={`auth-password-rule${isMatched ? ' is-met' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isMatched}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <span>{rule.label}</span>
                </label>
              )
            })}
          </div>
          <PasswordInput
            required
            label="Confirm new password"
            value={confirmPassword}
            onChange={(event, { value }) => setConfirmPassword(value)}
          />
        </Form>
        <Button
          primary={!hasPassword}
          negative={!!hasPassword}
          loading={isSavingPassword}
          disabled={
            isSavingPassword ||
            !newPassword ||
            !confirmPassword ||
            (hasPassword && !currentPassword)
          }
          onClick={savePassword}
        >
          {hasPassword ? 'Change password' : 'Set up password'}
        </Button>
      </div>
      <Confirm
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={() =>
          updateEmail({
            variables: { email },
          })
        }
        content={`You are changing your email address from ${advisor.email} to ${email}. We'll sign you out and send a verification link to your new address — you must verify before you can sign in again.`}
      />
    </div>
  )
}

export default Account
